import { Component } from '@theme/component';

const standard = { app_access: 'Free Strikeman app', requires_firearm: 'Yes', laser_cartridge_included: '1 standard laser cartridge', targets_included: '1 bullseye target', ejection_tool: 'Standard plastic ejection tool', carry_case: 'Not included' };
const marksman = { ...standard, targets_included: '1 three-piece target', ejection_tool: 'Premium extendable aluminum ejection tool', carry_case: 'Premium soft-shell travel case' };
const smartshot = { ...standard, laser_cartridge_included: '1 rechargeable laser / boresight' };
const cartridge = { app_access: 'Free Strikeman app', requires_firearm: 'Yes', laser_cartridge_included: '1 standard laser cartridge', targets_included: 'Not included', ejection_tool: 'See product details', carry_case: 'Not included' };
const catalog = [
  ['strikeman-laser-firearm-training-system', 'Standard Kit', standard],
  ['strikeman-marksman-kit', 'Marksman Kit', marksman],
  ['strikeman-laser-firearm-training-system-original-carry-case-kit', 'Standard + Carry Case', { ...standard, carry_case: 'Hard-shell carry case' }],
  ['marksman-kit-gift-bundle', 'Marksman Bonus Bundle', { ...marksman, laser_cartridge_included: '2 standard laser cartridges' }],
  ['ultimate-strikeman-gift-bundle', 'Ultimate Gift Bundle', { ...marksman, app_access: '1 year of Strikeman Pro included', laser_cartridge_included: '2 standard laser cartridges', targets_included: '1 bullseye target' }],
  ['strikeman-laser-cartridge', 'Standard laser cartridge', cartridge],
  ['smartshot', 'SmartShot laser / boresight', { ...cartridge, laser_cartridge_included: '1 rechargeable laser / boresight' }],
  ['strikeman-laser-bullet-cartridge-38-special-357-magnum-5-pack', '.38 Special / .357 Magnum 5-pack', { ...cartridge, laser_cartridge_included: '5 standard laser cartridges' }],
];
const products = new Map();
const node = (tag, text, className) => { const element = document.createElement(tag); if (text) element.textContent = text; if (className) element.className = className; return element; };

class HolidayComparison extends Component {
  #controller;
  #prices;
  #dialogProduct;
  #trigger;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const initial = JSON.parse(this.querySelector('[data-initial-products]').textContent);
    for (const [handle, product] of Object.entries(initial)) if (product?.id) products.set(handle, Promise.resolve(product));
    this.querySelectorAll('[data-compare-select]').forEach((select, index) => {
      const selected = select.value;
      select.replaceChildren(...catalog.map(([handle, name]) => new Option(name, handle, false, handle === selected)));
      select.addEventListener('change', () => this.#render(index), { signal });
      this.#render(index);
    });
    this.addEventListener('click', (event) => {
      const buy = event.target.closest('[data-buy]');
      if (buy) this.#open(Number(buy.dataset.buy), buy);
      if (event.target.closest('[data-close]')) this.querySelector('dialog').close();
    }, { signal });
    this.querySelector('dialog').addEventListener('close', () => this.#trigger?.focus(), { signal });
    this.querySelector('[data-variant]').addEventListener('change', () => this.#updateVariant(), { signal });
    this.querySelector('form').addEventListener('submit', (event) => {
      const variant = this.#dialogProduct?.variants.find((item) => String(item.id) === this.querySelector('[data-variant]').value);
      if (!variant?.available) event.preventDefault();
    }, { signal });
    this.#loadCampaign(signal);
  }

  disconnectedCallback() { super.disconnectedCallback(); this.#controller?.abort(); }

  async #product(handle) {
    if (!products.has(handle)) products.set(handle, fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`).then((response) => {
      if (!response.ok) throw new Error('Product unavailable');
      return response.json();
    }).catch((error) => { products.delete(handle); throw error; }));
    return products.get(handle);
  }

  async #loadCampaign(signal) {
    const code = this.dataset.campaignCode || 'FALL26';
    if ((window.Shopify?.currency?.active || this.dataset.currency) !== 'USD') return;
    try {
      const proxy = window.RegiosDOPP?.regiosDiscountsAppProxyUrl || '/apps/regios-discounts';
      const response = await fetch(`${proxy.replace(/\/$/, '')}/dopp/applied_code_product_discount_configs?codes=${encodeURIComponent(code)}`, { signal });
      if (!response.ok) return;
      const data = (await response.json()).byCode?.find((entry) => entry.kind === 'config' && entry.code.toUpperCase() === code.toUpperCase())?.discountAppData;
      const config = data?.config;
      const rule = config?.customLogic?.entryPoint;
      if (!config?.isEnabled || (data.startDate && Date.parse(data.startDate) > Date.now()) || (data.endDate && Date.parse(data.endDate) <= Date.now()) || config.customerEligibility?.type !== 'ALL_CUSTOMERS' || config.productEligibility?.type !== 'ALL_PRODUCTS' || config.minimumPurchaseRequirements?.requirementType !== 'NONE' || config.maximumPurchaseRequirements?.requirementType !== 'NONE' || rule?.type !== 'APPLY_DISCOUNT' || rule.value?.type !== 'PRICE_LIST' || rule.value.discountTemplate?.type !== 'FIXED_PRICE') return;
      this.#prices = rule.value.data;
      this.querySelectorAll('[data-compare-select]').forEach((_, index) => this.#render(index));
      if (this.#dialogProduct) this.#updateVariant();
    } catch { /* Native catalog pricing remains available without the campaign. */ }
  }

  #price(product, variant) {
    const currency = window.Shopify?.currency?.active || this.dataset.currency;
    const format = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(cents / 100);
    const variantEntry = this.#prices?.[variant.id];
    const productEntry = this.#prices?.[product.id];
    const entry = variantEntry?.resourceType === 'VARIANT_ID' ? variantEntry : productEntry?.resourceType === 'PRODUCT_ID' ? productEntry : null;
    const configured = Math.round(Number(entry?.discountValue) * 100);
    const campaignSale = Number.isFinite(configured) && configured >= 0 && configured < variant.price;
    const sale = campaignSale ? configured : variant.price;
    const original = Math.max(variant.compare_at_price || 0, variant.price);
    const discounted = original > sale;
    const wrap = node('div', '', `shc__price price-container--${discounted ? 'on-sale' : 'regular'}`);
    Object.assign(wrap.dataset, { regiosDoppGenericProductId: String(product.id), regiosDoppGenericVariantId: String(variant.id), priceOriginal: format(original), priceCompare: format(original), priceActual: format(sale), currencyCode: currency });
    const compare = node('s', format(original), 'shc__regular');
    compare.dataset.sppCompare = ''; compare.hidden = !discounted;
    const actual = node('span', format(sale), 'shc__sale'); actual.dataset.sppSale = '';
    wrap.append(compare, actual);
    if (campaignSale) wrap.append(node('small', `with code ${this.dataset.campaignCode || 'FALL26'}`));
    return wrap;
  }

  async #render(index) {
    const select = this.querySelector(`[data-compare-select="${index}"]`);
    const handle = select.value;
    const item = catalog.find(([key]) => key === handle);
    const buy = this.querySelector(`[data-buy="${index}"]`);
    buy.disabled = true;
    try {
      const product = await this.#product(handle);
      if (!this.isConnected || select.value !== handle) return;
      const url = `${window.Shopify?.routes?.root || '/'}products/${handle}`;
      const title = this.querySelector(`[data-name="${index}"]`); title.textContent = item[1]; title.href = url;
      const media = this.querySelector(`[data-media="${index}"] .shc__image`); media.href = url;
      const image = media.querySelector('img'); image.removeAttribute('srcset'); image.src = typeof product.featured_image === 'object' ? product.featured_image.src : product.featured_image; image.alt = product.title;
      if (this.dataset.bow && !media.querySelector('.shc__bow')) {
        const bow = node('img', '', 'shc__bow'); bow.src = this.dataset.bow; bow.alt = ''; bow.width = 44; bow.height = 44; bow.loading = 'lazy'; media.append(bow);
      }
      const variant = product.variants.find((entry) => entry.available) || product.variants[0];
      for (const row of this.querySelectorAll('[data-feature]')) {
        const cell = row.querySelector(`[data-cell="${index}"]`);
        if (row.dataset.feature === 'price') cell.replaceChildren(this.#price(product, variant));
        else if (row.dataset.feature === 'reviews') { const link = node('a', 'View reviews'); link.href = `${url}#reviews`; cell.replaceChildren(link); }
        else { const value = item[2][row.dataset.feature] || 'See product details'; cell.textContent = value; cell.classList.toggle('shc__yes', value === 'Yes'); }
      }
      buy.textContent = product.available ? 'Add to cart' : 'View availability'; buy.disabled = false;
      buy.setAttribute('aria-label', `${buy.textContent}: ${item[1]}`);
    } catch {
      if (select.value !== handle) return;
      this.querySelector('.shc__status').textContent = 'This product could not load. Please try another selection.';
      buy.disabled = true;
    }
  }

  async #open(index, trigger) {
    const handle = this.querySelector(`[data-compare-select="${index}"]`).value;
    this.#dialogProduct = await this.#product(handle);
    this.#trigger = trigger;
    this.querySelector('[data-dialog-title]').textContent = this.#dialogProduct.title;
    const select = this.querySelector('[data-variant]');
    const variants = this.#dialogProduct.variants;
    select.replaceChildren(new Option('Select an option', ''));
    for (const available of [true, false]) for (const variant of variants.filter((entry) => entry.available === available)) {
      const option = new Option(`${variant.title === 'Default Title' ? 'Default' : variant.title}${variant.available ? '' : ' (out of stock)'}`, String(variant.id));
      if (!variant.available) option.style.color = '#777';
      select.append(option);
    }
    if (variants.length === 1) select.value = String(variants[0].id);
    this.#updateVariant();
    this.querySelector('dialog').showModal();
  }

  #updateVariant() {
    const select = this.querySelector('[data-variant]');
    const product = this.#dialogProduct;
    const variant = product.variants.find((item) => String(item.id) === select.value);
    const button = this.querySelector('[data-submit]'); button.disabled = !variant?.available;
    const details = this.querySelector('[data-details]');
    const productUrl = `${window.Shopify?.routes?.root || '/'}products/${product.handle}${variant ? `?variant=${variant.id}` : ''}`;
    const hasCampaign = !!this.#prices;
    details.href = hasCampaign ? `/discount/${encodeURIComponent(this.dataset.campaignCode || 'FALL26')}?redirect=${encodeURIComponent(productUrl)}` : productUrl;
    details.textContent = variant && !variant.available ? 'Notify me on the product page' : 'View product';
    this.querySelector('input[name="return_to"]').value = hasCampaign ? `/discount/${encodeURIComponent(this.dataset.campaignCode || 'FALL26')}?redirect=/cart` : '/cart';
    this.querySelector('[data-stock]').textContent = variant && !variant.available ? 'This option is out of stock. Open the product page to request a notification.' : '';
    this.querySelector('[data-dialog-price]').replaceChildren(...(variant ? [this.#price(product, variant)] : []));
    if (variant) window.dispatchEvent(new CustomEvent('regios-dopp:variant-change', { detail: { selectedVariant: { id: Number(variant.id), price: Number(variant.price) }, quantity: 1 } }));
  }
}
if (!customElements.get('holiday-comparison')) customElements.define('holiday-comparison', HolidayComparison);



